import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import React from "react";
import { Header } from "./components/Header";
import { LanguageProvider } from "./i18n/LanguageProvider";
import { Hero } from "./components/Hero";
import { About } from "./components/About";
import { Experience } from "./components/Experience";
import { Articles } from "./components/Articles";
import { Projects } from "./components/Projects";
import { Contact } from "./components/Contact";
import { Footer } from "./components/Footer";
import { Testimonials } from "./components/Testimonials";

// Route-level chunks: nothing below the homepage ships in the first bundle.
const ArticlesPage = React.lazy(() => import("./components/ArticlesPage"));
const ArticleDetailPage = React.lazy(() => import("./components/ArticleDetailPage"));
const ProjectsPage = React.lazy(() => import("./components/ProjectsPage"));
const ProjectDetailPage = React.lazy(() => import("./components/ProjectDetailPage"));
const TestimonialsPage = React.lazy(() => import("./components/TestimonialsPage"));
const ChangelogPage = React.lazy(() => import("./components/ChangelogPage"));
const PortfolioChangelogPage = React.lazy(() => import("./components/PortfolioChangelogPage"));
const PersonalSpacePage = React.lazy(() => import("./components/PersonalSpacePage"));
const PersonalSpaceCategoryPage = React.lazy(() => import("./components/PersonalSpaceCategoryPage"));
const PersonalSpaceDetailPage = React.lazy(() => import("./components/PersonalSpaceDetailPage"));
const AdminRecommendations = React.lazy(() => import("./components/AdminRecommendations"));
const AdminPersonalSpace = React.lazy(() => import("./components/AdminPersonalSpace"));
const AdminMessages = React.lazy(() => import("./components/AdminMessages"));
const AIResourcesPage = React.lazy(() => import("./components/ai/AIResourcesPage"));
const AICategoryPage = React.lazy(() => import("./components/ai/AICategoryPage"));
const AIToolPage = React.lazy(() => import("./components/ai/AIToolPage"));

// Tuba Hoca — Standalone App (lazy loaded, completely isolated)
const TubaHocaApp = React.lazy(() => import("./components/tuba-hoca/TubaHocaApp"));

// Tuba Admin — Teacher CMS (lazy loaded, completely isolated)
const TubaAdminApp = React.lazy(() => import("./components/tuba-admin/TubaAdminApp"));

const HomePage = () => (
  <main>
    <Hero />
    <About />
    <Experience />
    <Projects />
    <Articles />
    <Testimonials />
    <Contact />
  </main>
);

/**
 * The localizable part of the site, declared once with relative paths so it can
 * be mounted under both "/" (English) and "/tr" (Turkish).
 */
const localizedRoutes = (
  <>
    <Route index element={<HomePage />} />
    <Route path="articles" element={<ArticlesPage />} />
    <Route path="articles/:slug" element={<ArticleDetailPage />} />
    <Route path="projects" element={<ProjectsPage />} />
    <Route path="projects/:slug" element={<ProjectDetailPage />} />
    <Route path="projects/:slug/changelog" element={<ChangelogPage />} />
    <Route path="ai-resources" element={<AIResourcesPage />} />
    <Route path="ai-resources/:categoryId" element={<AICategoryPage />} />
    <Route path="ai-resources/:categoryId/:toolId" element={<AIToolPage />} />
    <Route path="testimonials" element={<TestimonialsPage />} />
    <Route path="personal-space" element={<PersonalSpacePage />} />
    <Route path="personal-space/:categoryId" element={<PersonalSpaceCategoryPage />} />
    <Route path="personal-space/:categoryId/:itemId" element={<PersonalSpaceDetailPage />} />
    <Route path="changelog" element={<PortfolioChangelogPage />} />
  </>
);

// Portfolio shell — only renders Header/Footer on non-tuba-hoca routes
const PortfolioShell = () => (
  <LanguageProvider>
    <div className="grain-overlay min-h-screen bg-[#050505]">
      <Header />
      <React.Suspense fallback={<div className="min-h-screen" />}>
      <Routes>
        {/* English at the unprefixed path */}
        <Route path="/">{localizedRoutes}</Route>
        {/* Turkish under /tr */}
        <Route path="/tr">{localizedRoutes}</Route>
        {/* Internal tooling stays in English and is never prefixed */}
        <Route path="/admin/recommendations" element={<AdminRecommendations />} />
        <Route path="/admin/personal-space" element={<AdminPersonalSpace />} />
        <Route path="/admin/messages" element={<AdminMessages />} />
      </Routes>
      </React.Suspense>
      <Footer />
    </div>
  </LanguageProvider>
);

// Route-aware layout switcher
const AppLayout = () => {
  const location = useLocation();
  const isEnglishTime = location.pathname.startsWith('/english-time');
  const isTubaAdmin = location.pathname.startsWith('/tuba-admin');

  if (isEnglishTime) {
    return (
      <React.Suspense fallback={null}>
        <TubaHocaApp />
      </React.Suspense>
    );
  }

  if (isTubaAdmin) {
    return (
      <React.Suspense fallback={null}>
        <TubaAdminApp />
      </React.Suspense>
    );
  }

  return <PortfolioShell />;
};

function App() {
  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  );
}

export default App;
